import Feature from "ol/Feature";
import GeoJSON from "ol/format/GeoJSON";
import { Geometry } from "ol/geom";

/**
 * Converts an INLINE rule's area of interest between the API representation - a
 * GeoJSON GeometryCollection in EPSG:4326 - and the OpenLayers features shown on
 * the map in EPSG:3857. Shared by the rule form and the rule detail page.
 */

/**
 * Minimal structural view of the GeoJSON objects the API may return for an
 * area of interest. Only the discriminating fields are needed here.
 */
type GeoJsonLike = {
    type?: string;
    geometries?: unknown[];
};

export function areaOfInterestToFeatures(areaOfInterest: unknown): Feature<Geometry>[] {
    if (!areaOfInterest) {
        return [];
    }

    const geo = areaOfInterest as GeoJsonLike;

    const asFeatureCollection = geo.type === "GeometryCollection"
        ? {
            type: "FeatureCollection",
            features: (geo.geometries || []).map((geometry) => ({
                type: "Feature",
                properties: {},
                geometry,
            })),
        }
        : (geo.type === "FeatureCollection" || geo.type === "Feature" ? areaOfInterest : null);

    if (!asFeatureCollection) {
        console.warn("Unsupported area_of_interest GeoJSON type:", geo.type);
        return [];
    }

    return new GeoJSON().readFeatures(asFeatureCollection, { featureProjection: "EPSG:3857" });
}

export function featuresToAreaOfInterest(features: Feature<Geometry>[]) {
    const format = new GeoJSON();
    const geometries = features
        .map((feature) => feature.getGeometry())
        .filter((geometry): geometry is Geometry => Boolean(geometry))
        .map((geometry) => format.writeGeometryObject(geometry, {
            featureProjection: "EPSG:3857",
            dataProjection: "EPSG:4326",
        }));

    if (geometries.length === 0) {
        return null;
    }

    return {
        type: "GeometryCollection",
        geometries,
        crs: {
            type: "name",
            properties: { name: "EPSG:4326" },
        },
    };
}
